import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getLocalizedString } from "@icarius-localization/strings";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { Grid, Button, Tooltip } from "@material-ui/core";
import { AddIcon, DeleteIcon, EditIcon } from "@icarius-icons";
import { getAppColor } from "@icarius-app/selectors";
import { useSelector } from "react-redux";
import AgGridContainer from "@icarius-common/commonPage/agGridContainer";
import ConfirmationDialog from "../confirmationDialog";
import QuestionFormDialog from "../questionFormDialog";
import { getColumnDefByPage } from "@icarius-table/columnDefs";
import { Alert } from "@material-ui/lab";

// Grilla de preguntas predeterminadas con su alta, modificación y baja.
// El estado de los diálogos vive acá: el padre solo recibe la lista completa.
const QuestionsSection = ({ questions, onChange, disabled }) => {
    const dispatch = useDispatch();
    const color = useSelector(getAppColor);

    const questionsGridRef = useRef(null);
    const [questionDialogState, setQuestionDialogState] = useState({ open: false, index: null, data: null });
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

    const handleOpenQuestionDialog = (questionData = null, index = null) => {
        setQuestionDialogState({ open: true, index, data: questionData });
    };

    const handleCloseQuestionDialog = () => {
        setQuestionDialogState({ open: false, index: null, data: null });
    };

    const handleSaveQuestion = (questionData) => {
        const newQuestions = [...(questions || [])];
        if (questionDialogState.index !== null) {
            newQuestions[questionDialogState.index] = questionData;
        } else {
            newQuestions.push(questionData);
        }
        onChange(newQuestions);
        handleCloseQuestionDialog();
    };

    const handleOpenConfirmDialog = () => {
        const selectedNodes = questionsGridRef.current?.api?.getSelectedNodes();
        if (!selectedNodes || selectedNodes.length === 0) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }
        setOpenConfirmDialog(true);
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    const handleDeleteSelectedQuestions = () => {
        if (questionsGridRef.current?.api) {
            const selectedNodes = questionsGridRef.current.api.getSelectedNodes();
            const selectedIndexes = selectedNodes.map((node) => parseInt(node.id));
            const newQuestions = (questions || []).filter((_, index) => !selectedIndexes.includes(index));
            onChange(newQuestions);
            questionsGridRef.current.api.deselectAll();
        }
        handleCloseConfirmDialog();
    };

    const handleEditSelectedQuestion = () => {
        const selectedNodes = questionsGridRef.current?.api?.getSelectedNodes();
        if (!selectedNodes || selectedNodes.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectSingleItem"), severity: "warning" }));
            return;
        }
        const node = selectedNodes[0];
        handleOpenQuestionDialog(node.data, parseInt(node.id));
    };

    return (
        <>
            <Grid container item alignItems="center" xs={12} style={{ padding: "8px 10px", marginTop: 24 }}>
                <Grid
                    item
                    xs={12}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}
                >
                    <h3 style={{ margin: 0, color: "var(--mainText)", fontWeight: "normal", fontSize: "1rem" }}>
                        {getLocalizedString("defaultQuestions")}
                    </h3>
                    {!disabled && (
                        <div style={{ display: "flex", gap: 10 }}>
                            <Tooltip title={getLocalizedString("add")}>
                                <Button variant={"outlined"} onClick={() => handleOpenQuestionDialog()}>
                                    <AddIcon htmlColor={color} />
                                </Button>
                            </Tooltip>
                            <Tooltip title={getLocalizedString("modify")}>
                                <Button variant={"outlined"} onClick={handleEditSelectedQuestion}>
                                    <EditIcon htmlColor={color} />
                                </Button>
                            </Tooltip>
                            <Tooltip title={getLocalizedString("delete")}>
                                <Button variant={"outlined"} onClick={handleOpenConfirmDialog}>
                                    <DeleteIcon htmlColor={color} />
                                </Button>
                            </Tooltip>
                        </div>
                    )}
                </Grid>
                <Grid item xs={12}>
                    {disabled && (
                        <Alert variant="filled" severity="warning" style={{ marginBottom: 10 }}>
                            {getLocalizedString("questionsEnabledAfterFirstGeneration")}
                        </Alert>
                    )}
                </Grid>
                <Grid item xs={12}>
                    <AgGridContainer
                        ref={questionsGridRef}
                        ownColumnDef={getColumnDefByPage("queryDefinitionQuestions")}
                        rowData={questions || []}
                        gridTitle={getLocalizedString("defaultQuestions")}
                        height={250}
                        hasFixedFilters={false}
                    />
                </Grid>
            </Grid>
            <ConfirmationDialog
                open={openConfirmDialog}
                handleClose={handleCloseConfirmDialog}
                handleConfirm={handleDeleteSelectedQuestions}
                title={getLocalizedString("delete")}
                text={getLocalizedString("areYouSureYouWantToDeleteTheQuestion")}
            />
            <QuestionFormDialog
                open={questionDialogState.open}
                handleClose={handleCloseQuestionDialog}
                handleSave={handleSaveQuestion}
                initialData={questionDialogState.data}
                questions={questions || []}
                editIndex={questionDialogState.index}
            />
        </>
    );
};

export default QuestionsSection;
